import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RmmService } from '@services';
import { I18NextPipe } from 'angular-i18next';
import { cloneDeep, noop } from 'lodash';
import { MbsSize, ModalService, SortEvent } from 'mbs-ui-kit';
import { IncludeServices } from 'mbs-ui-shared/src/lib/models/rmm-custom-notification-model';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import ServiceInfo from 'mbs-ui-shared@models/rmm/ServiceInfo';
import { combineLatest, debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, take, tap } from 'rxjs';

export enum ServiceAlertType {
  Stop = 'stop',
  FailedStart = 'failedStart',
  FailedRestart = 'failedRestart'
}

export type AlertServiceInModal = Partial<ServiceInfo & IncludeServices>;

@UntilDestroy()
@Component({
  selector: 'mbs-select-service-modal',
  templateUrl: './rmm-select-service-modal.component.html'
})
export class RmmSelectServiceModalComponent implements OnChanges {
  @Input() enabled = false;
  @Input() appendTo?: string;
  @Input() includedServices: AlertServiceInModal[] = [];
  @Output() onServiceSelectionChanged = new EventEmitter<IncludeServices[]>();

  @ViewChild('selectServiceModalBody', { static: true }) selectServiceModalBody: TemplateRef<any>;
  @ViewChild('addServiceFormBody', { static: true }) addServiceFormBody: TemplateRef<any>;

  public serviceAlertType = ServiceAlertType;
  public virtualScrolling = false;
  private readonly virtualScrollingSize = 300;
  public headers = [
    {
      name: 'Display Name',
      sort: 'serviceName',
      overflow: true,
      gridColSize: '30fr',
      inService: true
    },
    {
      name: 'Name',
      sort: 'name',
      overflow: true,
      gridColSize: '30fr',
      inService: true
    },
    {
      name: 'Stopped',
      sort: 'stop',
      gridColMin: '100px',
      gridColSize: '10fr',
      class: '-center'
    }
    // hidden until rmm23
    // {
    //   name: 'Failed to Start',
    //   sort: 'failedStart',
    //   gridColMin: '100px',
    //   gridColSize: '10fr',
    //   class: '-center'
    // },
    // {
    //   name: 'Failed to Restart',
    //   sort: 'failedRestart',
    //   gridColMin: '100px',
    //   gridColSize: '10fr',
    //   class: '-center'
    // }
  ];

  public serviceHeaders = this.headers.filter((header) => header.inService);

  public form = new UntypedFormGroup({
    computerSelection: new FormControl(null),
    serviceSearch: new FormControl(''),
    includedServiceSearch: new FormControl('')
  });

  public addServiceForm = new UntypedFormGroup({
    serviceDisplayName: new FormControl(''),
    serviceName: new FormControl('')
  });

  public data: AlertServiceInModal[];
  public includedData: AlertServiceInModal[];
  private selectedIncludedServices: AlertServiceInModal[] = [];

  public selectionFromList: AlertServiceInModal[] = [];
  public selectionFromIncludeList: AlertServiceInModal[] = [];

  public selectedComputerHid = null;

  private orderBy: SortEvent = { column: 'name', direction: 'asc' };
  private includedOrderBy: SortEvent = { column: 'name', direction: 'asc' };
  public loading = false;

  constructor(
    private modalService: ModalService,
    public rmmService: RmmService,
    private i18n: I18NextPipe,
    private computersFacade: ComputersFacade
  ) {
    this.setInitialComputer();

    const services$ = this.form.get('computerSelection').valueChanges.pipe(
      distinctUntilChanged((previous, current) => previous?.hid === current?.hid),
      tap(() => (this.loading = true)),
      switchMap((computer) => computer && this.rmmService.fetchLast<ServiceInfo[]>('service', computer.hid)),
      map((services) => services.data)
    );

    const searchValue$ = this.form.get('serviceSearch').valueChanges.pipe(debounceTime(300));

    combineLatest([services$, searchValue$])
      .pipe(
        map(([services, searchValue]) => {
          const updatedServices = services.map((service) => this.prepareServiceDataForTable(service));
          return [updatedServices, searchValue];
        }),
        map(([services, searchValue]) => this.filterTableData(searchValue, services)),
        map((services) => this.sortTable(this.orderBy, services)),
        untilDestroyed(this)
      )
      .subscribe((services) => {
        this.data = services;
        this.virtualScrolling = services?.length >= this.virtualScrollingSize;
        this.loading = false;
      });

    this.form
      .get('includedServiceSearch')
      .valueChanges.pipe(
        debounceTime(300),
        map((searchValue) => this.filterTableData(searchValue, cloneDeep(this.selectedIncludedServices))),
        map((services) => this.sortTable(this.includedOrderBy, services)),
        untilDestroyed(this)
      )
      .subscribe((services) => {
        this.includedData = services;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.includedServices && changes.includedServices.currentValue) this.refreshIncludedData();
  }

  refreshIncludedData(): void {
    this.selectedIncludedServices = cloneDeep(this.includedServices);
    this.form.get('includedServiceSearch').setValue('');
  }

  private filterTableData(searchValue: string, services: AlertServiceInModal[]): AlertServiceInModal[] {
    return !searchValue || searchValue === ''
      ? services
      : services.filter(
          (item) =>
            item?.serviceName.toLowerCase().includes(searchValue.toLowerCase()) ||
            (item?.name && item.name.toLowerCase().includes(searchValue.toLowerCase())) ||
            (item?.displayName && item.displayName.toLowerCase().includes(searchValue.toLowerCase()))
        );
  }

  private sortTable(orderBy: SortEvent, services: AlertServiceInModal[]): AlertServiceInModal[] {
    if (!services.length) return services;
    switch (orderBy.direction) {
      case 'asc':
        return services.sort((a, b) => (a[orderBy.column] > b[orderBy.column] ? 1 : -1));
      case 'desc':
        return services.sort((a, b) => (a[orderBy.column] > b[orderBy.column] ? -1 : 1));
      default:
        return services;
    }
  }

  private prepareServiceDataForTable(service: AlertServiceInModal): AlertServiceInModal {
    const item = this.selectedIncludedServices?.find(
      (included) => included.serviceName === service.name || included.serviceName === service.displayName
    );

    if (item) {
      return { ...service, ...item };
    }

    return {
      ...service,
      serviceDisplayName: service.serviceDisplayName ?? service.displayName ?? service.name,
      serviceName: service.serviceName ?? service.name ?? service.displayName,
      failedRestart: false,
      failedStart: false,
      stop: true
    };
  }

  handleSort(event: SortEvent): void {
    this.orderBy = { ...event };
    this.form.get('serviceSearch').setValue(this.form.get('serviceSearch').value);
  }

  handleIncludedSort(event: SortEvent): void {
    this.includedOrderBy = { ...event };
    this.triggerIncludedTableUpdate();
  }

  openSelectServiceModal(): void {
    this.form.get('serviceSearch').setValue('');
    this.refreshIncludedData();

    this.modalService
      .open(
        {
          header: {
            title: this.i18n.transform('mbs-ui:rmm-custom-alerting.serviceModal.title'),
            showExpandedCross: true
          },
          footer: {
            okButton: {
              text: this.i18n.transform('shared:buttons.save')
            }
          },
          size: MbsSize.xl
        },
        this.selectServiceModalBody
      )
      .then(() => {
        this.onServiceSelectionChanged.emit(
          this.selectedIncludedServices.map((service) => ({
            serviceDisplayName: service.serviceDisplayName,
            serviceName: service.serviceName,
            failedRestart: service.failedRestart,
            failedStart: service.failedStart,
            stop: service.stop
          }))
        );
      })
      .catch(noop);
  }

  handleChangeSelectedExamples(selectedData: AlertServiceInModal[]): void {
    this.selectionFromList = [...selectedData];
  }
  handleChangeSelectedServices(selectedData: AlertServiceInModal[]): void {
    this.selectionFromIncludeList = [...selectedData];
  }

  updateIncludedServicesList(state: boolean, keyName: ServiceAlertType, item: AlertServiceInModal): void {
    const includedItem = this.selectedIncludedServices?.find((service) =>
      [item.serviceName, item?.displayName, item?.name].includes(service.serviceName)
    );

    if (includedItem) {
      includedItem[keyName] = state;
    } else {
      item[keyName] = state;
      this.selectedIncludedServices.push(item);
    }
  }

  private setInitialComputer(): void {
    this.computersFacade.loadData();
    this.computersFacade.getDataWhenLoaded$
      .pipe(
        take(1),
        filter((items) => !!items.length),
        untilDestroyed(this)
      )
      .subscribe((computers) => {
        this.selectedComputerHid = computers[0].hid;
      });
  }

  addToIncludeList(): void {
    this.selectedIncludedServices = this.selectedIncludedServices.concat(
      this.selectionFromList.filter(
        (service) => !this.selectedIncludedServices.some((included) => included.serviceName === service.serviceName)
      )
    );
    this.selectionFromList = [];
    this.triggerIncludedTableUpdate();
  }

  removeFromIncludeList(): void {
    this.selectedIncludedServices = this.selectedIncludedServices.filter(
      (service) => !this.selectionFromIncludeList.some((selection) => selection.serviceName === service.serviceName)
    );
    this.selectionFromIncludeList = [];
    this.triggerIncludedTableUpdate();
  }

  addNewService(): void {
    this.modalService
      .open(
        {
          header: {
            title: this.i18n.transform('mbs-ui:rmm-custom-alerting.serviceModal.addServiceModalTitle'),
            showExpandedCross: false
          },
          size: MbsSize.md,
          footer: {
            okButton: {
              text: this.i18n.transform('shared:buttons.save'),
              disabled$: this.addServiceForm.get('serviceDisplayName').valueChanges.pipe(
                startWith(''),
                map((value) => !value.length),
                untilDestroyed(this)
              )
            }
          }
        },
        this.addServiceFormBody
      )
      .then(() => {
        const displayName = this.addServiceForm.get('serviceDisplayName').value;
        const name = this.addServiceForm.get('serviceName').value;

        const isAlreadyExist = this.selectedIncludedServices.some(
          (service) => displayName === service.displayName || name === service.displayName
        );

        if (!isAlreadyExist) {
          this.selectedIncludedServices.push({
            displayName,
            name,
            serviceDisplayName: displayName ?? name,
            serviceName: name ?? displayName,
            failedRestart: false,
            failedStart: false,
            stop: true
          });
          this.triggerIncludedTableUpdate();
        }
      })
      .catch(noop);
  }

  triggerIncludedTableUpdate(): void {
    this.form.get('includedServiceSearch').setValue(this.form.get('includedServiceSearch').value);
  }
}
